import React from 'react';
import { useSelector } from 'react-redux';

import { T, FONT_STACK } from '../containers/customer/customers.theme';
import { CustomersKeyframes } from '../containers/customer/CustomersUI';
import IncomeStatement from '../containers/reports/IncomeStatement';
import SalesSummary from '../containers/reports/SalesSummary';

/**
 * Reports.
 *
 * Built on Administration's shape — a strip of sub-tabs across the top and one
 * report underneath — because the server has five report endpoints and only
 * one of them has a screen so far. A tab strip with one tab looks deliberate
 * where a bare page would have to be rebuilt when the second arrives; adding
 * the cash-flow reports later means one entry in TABS and one component.
 *
 * Each report owns its own date range and its own fetch. They are read at
 * different periods — a shift report is a day, an income statement a month —
 * so a shared range control would fight whoever used it last.
 */

const TABS = [
  { id: 'income', label: 'Income statement' },
  { id: 'sales', label: 'Sales summary' },
];

/** The roles the report routes let through: manager and accountant. */
const ALLOWED_ROLES = ['manager', 'accountant', 'superadmin-superadmin'];

export default function Reports() {
  const activeUser = useSelector((state) => state.login.activeUser);
  const [tab, setTab] = React.useState('income');

  const allowed = ALLOWED_ROLES.indexOf(activeUser?.role) !== -1;

  if (!allowed) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <span style={{ fontSize: 14, color: T.inkSoft }}>
          Only managers can view this page.
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <CustomersKeyframes />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '18px 28px 0',
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        {TABS.map((entry) => {
          const active = tab === entry.id;
          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              style={{
                padding: '0 4px 14px',
                margin: '0 16px 0 0',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? T.brand : 'transparent'}`,
                font: 'inherit',
                fontSize: 15,
                fontWeight: active ? 700 : 500,
                color: active ? T.ink : T.inkFaint,
                cursor: 'pointer',
              }}
            >
              {entry.label}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 28px 32px' }}>
        {tab === 'income' ? <IncomeStatement /> : null}
        {tab === 'sales' ? <SalesSummary /> : null}
      </div>
    </div>
  );
}
